import PropTypes from 'prop-types';
import React, { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import normalizeProjectStoreNavList from '@Hub/Header/utils/normalizeProjectStoreNavList';
import SearchTrigger from '@Hub/Search/Trigger';

import { AppMetaContext, ProjectContext, EnterpriseParentContext } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import useFeatureOptions from '@core/hooks/useFeatureOptions';
import useMediaQuery from '@core/hooks/useMediaQuery';
import { useProjectStore } from '@core/store';

import LandingHeader from '@routes/Landing/Header';

import classes from '../../style.module.scss';
import { useDarkMode, useFeatureFilter } from '../../utils/hooks';
import JumpToButton from '../JumpToButton';
import LinkItem from '../LinkItem';
import LogoLink from '../LogoLInk';
import MobileTopNav from '../mobile/TopNav';
import PageCategory from '../PageCategory';
import Search from '../Search';
import StagingBanner from '../StagingBanner';
import ThemeOverride from '../ThemeOverride';
import UserMenu from '../UserMenu';

const AppTopNav = ({ toggleSearch, lang, version, isDetachedProductionSite, setTheme, ...props }) => {
  const { project } = useContext(ProjectContext);
  const { appearance, modules } = project;
  const appMeta = useContext(AppMetaContext);
  const enterprise = useContext(EnterpriseParentContext);
  const { pathname, search } = useLocation();
  const featureOptions = useFeatureOptions(modules);
  const userScheme = useDarkMode();
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const isLP = ['/', ''].includes(pathname);
  const isLPWithSearch = isLP && appearance?.promos?.[0]?.extras?.type === 'search';
  const { parent } = project;
  const bem = useClassy(classes, 'Header');

  const colorScheme = useProjectStore(s => s.data.appearance.brand.theme);
  const projectTheme = parent ? parent.appearance.colorScheme : colorScheme;

  const owlbotTrial = new URLSearchParams(search).get('owlbotTrial');
  const owlbotEnabled = project.owlbot.enabled || owlbotTrial === 'true';

  const [topNavRight, topNavLeft, projectLayout, isFullWidth] = useProjectStore(s => [
    normalizeProjectStoreNavList(s.data.appearance.navigation.right),
    normalizeProjectStoreNavList(s.data.appearance.navigation.left),
    s.data.appearance.layout.style,
    s.data.appearance.layout.full_width === 'enabled',
  ]);

  const makeTopNavItems = (item, i) => {
    // TODO-HUB1-MIGRATION
    // Remove hub1 things from the topnav
    // We should do a migration to remove these once we remove hub1
    const filterTopNav = ['search', 'version', 'blog'];
    if (filterTopNav.includes(item.type)) return undefined;
    const TagType = item.type !== 'user' ? LinkItem : UserMenu;
    return <TagType key={`tagtype-${item.type}-${i}`} item={item} />;
  };

  // the GLP for single project enterprises should use the nav items in the parent
  const topRightItems = isLP && project.flags.singleProjectEnterprise ? project.parent.topnav.right : topNavRight;
  const topLeftItems = isLP && project.flags.singleProjectEnterprise ? project.parent.topnav.left : topNavLeft;
  const leftTopNavItems = useFeatureFilter(topLeftItems).map(makeTopNavItems);
  const rightTopNavItems = useFeatureFilter(topRightItems).map(makeTopNavItems);

  // setting the initial theme for header RDMD blocks (only used for Owlbot atm)
  useEffect(() => setTheme(userScheme === 'system' ? (prefersDarkMode ? 'dark' : 'light') : userScheme));

  return (
    <div className={bem('rm-Header-top', '-top', `-top_${projectLayout}`)}>
      <StagingBanner isDetachedProductionSite={isDetachedProductionSite} />
      <div className={bem('rm-Container', 'rm-Container_flex', isFullWidth && 'rm-ContainerFull')}>
        <JumpToButton />
        <div className={bem('rm-Header-left', '-left', projectLayout === 'modern' && '-left_expand')}>
          <LogoLink />
          {leftTopNavItems}
        </div>
        <div className={bem('rm-Header-left', '-left', '-left_mobile')}>
          <MobileTopNav lang={lang} version={version} />
          <PageCategory appMeta={appMeta} featureOptions={featureOptions} />
        </div>
        {projectLayout === 'modern' && (
          <div className={bem('rm-Header-center', '-center')}>
            <Search
              childSubdomain={project.parent ? project.subdomain : undefined}
              isPrimary
              toggleSearch={toggleSearch}
              {...props}
            />
          </div>
        )}
        <div className={bem('rm-Header-right', '-right', projectLayout === 'modern' && '-right_expand')}>
          <span className={bem('-right_desktop')}>{rightTopNavItems}</span>
          {/* Keep the existing search trigger for classic themes to avoid messing with custom CSS */}
          {!isLPWithSearch && !owlbotEnabled && (
            <SearchTrigger className={bem('-search')} toggleSearch={toggleSearch}>
              <div className="rm-SearchToggle-icon icon-search1" />
            </SearchTrigger>
          )}
          {projectTheme === 'system' && <ThemeOverride setTheme={setTheme} />}
        </div>
      </div>
      {projectLayout === 'classic' && !!isLP && !enterprise.isParent && <LandingHeader toggleSearch={toggleSearch} />}
    </div>
  );
};

AppTopNav.propTypes = {
  isDetachedProductionSite: PropTypes.bool.isRequired,
  lang: PropTypes.string,
  setTheme: PropTypes.func,
  toggleSearch: PropTypes.func,
  version: PropTypes.object,
};

export default AppTopNav;
